import { CopyOutlined } from '@ant-design/icons';
import { Input, InputNumber, Modal, message } from 'antd';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { inviteCodeStore } from '@/stores';

interface GenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GenerateDialog({ open, onOpenChange }: GenerateDialogProps) {
  const [count, setCount] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setCount(1);
      setGeneratedCodes([]);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (count < 1) {
      return;
    }

    setLoading(true);
    try {
      const reply = await inviteCodeStore.generateInviteCode({ count });
      const codes = reply.invite_codes
        .map((inviteCode) => inviteCode.code)
        .filter(Boolean);
      if (codes.length === 0) {
        throw new Error('服务端未返回生成的邀请码');
      }
      setGeneratedCodes(codes);
      message.success(`成功生成 ${codes.length} 个邀请码`);
    } catch (error) {
      console.error('生成邀请码失败:', error);
      message.error('生成邀请码失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAll = async () => {
    if (generatedCodes.length === 0) {
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedCodes.join('\n'));
      message.success(`已复制 ${generatedCodes.length} 个邀请码`);
    } catch (error) {
      console.error('复制邀请码失败:', error);
      message.error('复制失败，请手动选择邀请码');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!loading) {
          onOpenChange(false);
        }
      }}
      closable={!loading}
      keyboard={!loading}
      maskClosable={!loading}
      title={generatedCodes.length > 0 ? '邀请码已生成' : '生成邀请码'}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {generatedCodes.length > 0 ? '关闭' : '取消'}
          </Button>
          {generatedCodes.length > 0 ? (
            <Button onClick={handleCopyAll}>
              <CopyOutlined className="mr-2" />
              复制全部
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                background: 'linear-gradient(135deg, #ff5c5c 0%, #ff7070 100%)',
                color: '#ffffff',
                boxShadow:
                  '0 4px 12px rgba(255, 92, 92, 0.4), 0 0 0 1px rgba(255, 92, 92, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
              className="hover:opacity-90 hover:shadow-lg transition-all font-semibold"
            >
              {loading ? '生成中...' : '生成'}
            </Button>
          )}
        </div>
      }
      centered
      styles={{
        container: {
          padding: '24px',
          borderRadius: '8px',
          backgroundColor: 'var(--card)',
          border: '1px solid var(--border)',
        },
      }}
    >
      {generatedCodes.length > 0 ? (
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            已生成 {generatedCodes.length} 个邀请码，每行一个，可一键复制全部。
          </p>
          <Input.TextArea
            aria-label="本次生成的邀请码"
            value={generatedCodes.join('\n')}
            readOnly
            rows={Math.min(Math.max(generatedCodes.length, 3), 12)}
            className="font-mono"
          />
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mb-4">
            请输入要生成的邀请码数量
          </p>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="count">数量</Label>
              <InputNumber
                id="count"
                min={1}
                max={100}
                value={count}
                onChange={(value) => setCount(value || 1)}
                placeholder="请输入数量"
                className="w-full"
              />
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
